<template>

<form
  class="mx-auto col-sm-12"
  @submit.prevent
>
  <b-alert
    :show="alert.show"
    @dismissed='alert.show = false'
    dismissible
    fade
    :variant="alert.type"
  >
    {{ alert.message }}
  </b-alert>

  <div class="row">
    <div class="form-group col-lg-4" :class="{ 'has-error': errors.has('serie') }">
      <label for="serie">{{ $t('serie') }}</label>
      <div class="input-group">
        <input
          type="text"
          id="serie"
          name="serie"
          class="form-control"
          :placeholder="$t('ph_serie')"
          v-model="equipment.serial"
          v-validate="`required`"
        />

        <div class="input-group-append">
          <button
            class="btn btn-primary"
            type="button"
            @click="getEquipmentData"
          >
            <i class="fas fa-search" />
          </button>
        </div>
      </div>

      <small v-show="errors.has('serie')" class="form-text text-muted">
        {{ errors.first('serie') }}
      </small>
    </div>

    <div class="form-group col-lg-4" :class="{ 'has-error': errors.has('type') }">
      <label for="type">{{ $t('type') }}</label>
      <input
        disabled
        type="text"
        id="type"
        name="type"
        class="form-control"
        :placeholder="$t('ph_type')"
        v-model="equipment.type"
      />
      
    </div>

    <div class="form-group col-lg-4" :class="{ 'has-error': errors.has('version') }">
      <label for="version">{{ $t('version') }}</label>
      <input
        disabled
        type="text"
        id="version"
        name="version"
        class="form-control"
        :placeholder="$t('ph_version')"
        v-model="equipment.version"
      />
      
    </div>
  </div>

  <div class="row">
    <div class="form-group col-lg-12" :class="{ 'has-error': errors.has('client') }">
      <label for="client">{{ $t('client') }}</label>
      <Combobox
        class="bg-white"
        name='client'
        :placeholder="$t('select_a_item')"
        :hasErrors="errors.has('client')"
        :options="clients"
        :initial-value="equipment.client"
        label="CLI_DSC"
        v-model="equipment.client"
        :reduce="client => client.CLI_ID"
      />

    </div>
  </div>

  <div class="row">
    <div class="form-group col-lg-4" :class="{ 'has-error': errors.has('area_code') }">
      <label for="area_code">{{ $t('area_code') }}</label>
      <input
        type="text"
        id="area_code"
        name="area_code"
        class="form-control"
        :placeholder="$t('ph_area_code')"
        v-model="equipment.area_code"
      />
      
    </div>

    <div class="form-group col-lg-4" :class="{ 'has-error': errors.has('phone') }">
      <label for="phone">{{ $t('phone') }}</label>
      <the-mask
        type="text"
        id="phone"
        name="phone"
        class="form-control"
        :placeholder="$t('ph_phone')"
        v-model="equipment.phone_number"
        :mask="'9####-####'"
        masked
      />
      
    </div>

    <div class="form-group col-lg-4" :class="{ 'has-error': errors.has('PIN') }">
      <label for="PIN">{{ $t('PIN') }}</label>
      <input
        type="text"
        id="PIN"
        name="PIN"
        class="form-control"
        :placeholder="$t('ph_pin')"
        v-model="equipment.pin"
      />
      
    </div>
  </div>

  <div class="row">
    <div class="form-group col-lg-4" :class="{ 'has-error': errors.has('PUK') }">
      <label for="PUK">{{ $t('PUK') }}</label>
      <input
        type="text"
        id="PUK"
        name="PUK"
        class="form-control"
        :placeholder="$t('ph_puk')"
        v-model="equipment.puk"
      />
      
    </div>

    <div class="form-group col-lg-4" :class="{ 'has-error': errors.has('operator') }">
      <label for="operator">{{ $t('operator') }}</label>
      <input
        type="text"
        id="operator"
        name="operator"
        class="form-control"
        :placeholder="$t('ph_operator')"
        v-model="equipment.operator"
      />
      
    </div>

    <div class="form-group col-lg-4" :class="{ 'has-error': errors.has('phone_serial') }">
      <label for="phone_serial">{{ $t('phone_serial') }}</label>
      <input
        type="text"
        id="phone_serial"
        name="phone_serial"
        class="form-control"
        :placeholder="$t('ph_phone_serial')"
        v-model="equipment.phone_serial"
      />
      
    </div>
  </div>

  <div class="form-group row">
    <div class="col-sm-4 col-sm-offset-2">
      <LoaderButton
        @click="save"
      >
        {{ $t('save') }}
      </LoaderButton>
    </div>
  </div>
</form>

</template>

<script>
import { mapState, mapActions } from 'vuex'
import LoaderButton from '@/components/button/LoaderButton'
import Combobox from '@/components/shared/ComboBox'
import { request } from '@/lib/http'

export default {
  name: 'MovementTypeForm',
  components: {
    LoaderButton,
    Combobox
  },

  data() {
    return {
      clients : [],
      alert   : {
        show    : false,
        message : '',
        type    : ''
      },
      equipment: {
        serial        : '',
        type          : '',
        version       : '',
        client        : undefined,
        area_code     : '',
        phone_number  : '',
        pin           : '',
        puk           : '',
        operator      : '',
        phone_serial  : ''
      }
    }
  },

  methods: {
    clear() {
      this.equipment.client       = undefined
      this.equipment.area_code    = ''
      this.equipment.phone_number = ''
      this.equipment.pin          = ''
      this.equipment.puk          = ''
      this.equipment.operator     = ''
      this.equipment.phone_serial = ''
      this.equipment.type         = ''
      this.equipment.version      = ''
    },

    async getEquipmentData () {
      this.alert.show = false

      const [data] = await request({
        url     : `equipments/${this.equipment.serial}`,
        method  : 'get'
      })

      if (data) {
        this.equipment.client       = data.CLI_ID
        this.equipment.area_code    = data.EQP_DDD
        this.equipment.phone_number = data.EQP_TEL
        this.equipment.pin          = data.EQP_PIN
        this.equipment.puk          = data.EQP_PUK
        this.equipment.operator     = data.EQP_OPC
        this.equipment.phone_serial = data.EQP_SRT
        this.equipment.type         = data.VER_COD
        this.equipment.version      = data.VER_DSC
      } else {
        this.clear()
        this.alert.show    = true
        this.alert.type    = 'danger'
        this.alert.message = 'Serial não encontrado!'
      }
    },

    async save () {
      this.alert.show = false

      const valid = await this.$validator.validateAll()
      if (!valid) {
        const err = new Error(this.$t('invalid_form_fields'))
        this.$store.dispatch('notify/error', err)
        return
      }

      const phoneValid = new RegExp(/\9\d{4}\-\d{4}/)
      
      if (!phoneValid.test(this.equipment.phone_number)) {
        this.alert.show = true
        this.alert.type = 'danger'
        this.alert.message = 'Telefone inválido!'
        return
      }

      const { edited } = await request({
        url     : `equipments/${this.equipment.serial}`,
        method  : 'patch',
        data    : this.equipment
      })

      if (edited === 'notFound') {
        this.alert.show    = true
        this.alert.type    = 'danger'
        this.alert.message = 'Serial não encontrado!'
        return
      }

      if (edited) {
        this.clear()
        this.equipment.serial = ''
        this.alert.show    = true
        this.alert.type    = 'success'
        this.alert.message = 'Dados alterados com sucesso!'
        this.$router.go(0)
      } else {
        this.alert.show    = true
        this.alert.message = 'Erro ao editar dados!'
      }
    }
  },

  async mounted() {
    this.clients = await request({
      url     : `clients`,
      method  : 'get'
    })
  }
}
</script>